/**
 * Os dois avisos da tela do chamado aberto.
 *
 * ## O pedido (Gisele, 23/10/2026)
 *
 * "Identificar quando outro usuário estiver visualizando ou editando o mesmo
 * ticket, evitando que duas pessoas façam alterações ao mesmo tempo sem
 * perceber."
 *
 * "Caso outro usuário realize alguma alteração enquanto o ticket estiver
 * aberto, apresentar um aviso informando que existem novas informações e
 * disponibilizar a opção 'Atualizar Ticket' para carregar os dados mais
 * recentes antes de continuar a edição."
 *
 * ## Por que um módulo
 *
 * `TicketDetails` tem mais de 7.000 linhas e nenhum teste de componente. O que
 * pode errar em silêncio aqui é a REDAÇÃO e o momento: avisar com o mesmo
 * destaque quem só está lendo (e ensinar o operador a ignorar o aviso),
 * dizer "está editando" de alguém que parou dez minutos atrás, ou gritar
 * "há informações novas" por causa do salvamento que o próprio operador
 * acabou de fazer.
 */

/** Ninguém a mais no chamado. */
export const SEM_NINGUEM = { tipo: 'ninguem', texto: '', nomes: [] };

/**
 * Quem mais está no chamado, em uma frase.
 *
 * @param {{name: string, editando: boolean}[]} outros  o que o batimento devolveu
 * @returns {{tipo: 'ninguem'|'vendo'|'editando', texto: string, nomes: string[]}}
 */
export const avisoDePresenca = (outros) => {
    const lista = (Array.isArray(outros) ? outros : [])
        .filter((p) => p && String(p.name || '').trim() !== '');

    if (lista.length === 0) return SEM_NINGUEM;

    /*
     * Quem está EDITANDO manda na frase, mesmo sendo minoria: ler junto não é
     * problema, e é a edição simultânea que o pedido quer evitar. Com uma
     * frase só para os dois casos, o aviso que importa se perderia no meio dos
     * que não importam.
     */
    const editando = lista.filter((p) => p.editando);
    const relevantes = editando.length > 0 ? editando : lista;
    const nomes = relevantes.map((p) => String(p.name).trim());

    const quem = nomes.length === 1
        ? nomes[0]
        : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;

    if (editando.length > 0) {
        return {
            tipo: 'editando',
            nomes,
            texto: nomes.length === 1
                ? `${quem} está editando este chamado agora.`
                : `${quem} estão editando este chamado agora.`,
        };
    }

    return {
        tipo: 'vendo',
        nomes,
        texto: nomes.length === 1
            ? `${quem} também está com este chamado aberto.`
            : `${quem} também estão com este chamado aberto.`,
    };
};

/**
 * O INSTANTE de um carimbo, em segundos inteiros.
 *
 * ## Por que não dá para comparar o texto (medido em produção, 26/10/2026)
 *
 * As duas pontas falam do mesmo momento em formatos diferentes:
 *
 *   o GET do chamado:  2026-09-03T04:50:11.000000Z   (UTC, com microssegundos)
 *   o batimento:       2026-09-03T01:50:11-03:00     (com o fuso)
 *
 * Comparando como texto eles NUNCA são iguais, e o aviso "há informações
 * novas" nascia ligado em todo chamado, para todo operador -- exatamente o
 * aviso-que-aparece-sempre que este módulo existe para evitar.
 *
 * ## Por que SEGUNDOS inteiros
 *
 * O carimbo do banco não tem fração (o `timestamp` do MySQL guarda segundos),
 * mas o GET imprime `.000000` e o batimento não imprime nada. Comparar em
 * milissegundos deixaria a porta aberta para a mesma classe de erro no dia em
 * que uma das pontas passar a carregar fração -- e uma alteração dentro do
 * mesmo segundo não é detectável de qualquer forma.
 */
const emSegundos = (carimbo) => {
    if (!carimbo) return null;

    const milissegundos = new Date(carimbo).getTime();

    // Carimbo ilegível não vira novidade: avisar por causa de um texto que
    // ninguém sabe ler é pior que não avisar.
    if (Number.isNaN(milissegundos)) return null;

    return Math.floor(milissegundos / 1000);
};

/**
 * O chamado mudou desde que esta tela o carregou?
 *
 * @param {string|null} conhecido  o carimbo que a tela tem em mãos
 * @param {string|null} doServidor o carimbo que o batimento devolveu
 */
export const temNovidade = (conhecido, doServidor) => {
    const depois = emSegundos(doServidor);

    // Sem carimbo do servidor não dá para afirmar nada. Avisar aqui seria
    // avisar por causa de uma falha de rede.
    if (depois === null) return false;

    /*
     * A tela ainda não sabe de nada (primeiro batimento depois de um F5, ou
     * chamado recém-aberto). O primeiro carimbo vira a REFERÊNCIA -- tratá-lo
     * como novidade faria o aviso aparecer em toda abertura de chamado.
     */
    const antes = emSegundos(conhecido);

    if (antes === null) return false;

    return antes !== depois;
};

/**
 * O que a tela deve guardar como "carimbo conhecido" depois de um batimento.
 *
 * Adota o primeiro carimbo e mantém o que já tinha depois disso -- é o
 * salvamento (meu ou o "Atualizar Ticket") que avança a referência, não o
 * batimento. Sem esta regra, o aviso de novidade sumiria sozinho no batimento
 * seguinte, antes de alguém ter clicado em nada.
 */
export const proximoCarimbo = (conhecido, doServidor) => {
    if (!doServidor) return conhecido;
    if (!conhecido) return doServidor;

    return conhecido;
};

/**
 * De quanto em quanto tempo a tela bate, em milissegundos.
 *
 * Menos da metade dos 60 s que o servidor usa como validade: assim uma batida
 * perdida (rede ruim, aba de fundo suspensa pelo navegador) não faz a pessoa
 * piscar na tela de quem está olhando. Aviso que aparece e some sozinho é pior
 * que nenhum, porque ninguém aprende a confiar nele.
 */
export const INTERVALO_DO_BATIMENTO = 25000;

/**
 * Por quanto tempo depois de uma tecla a pessoa conta como "editando".
 *
 * O batimento manda `editando: true` enquanto a última alteração estiver
 * dentro desta janela. Maior que o intervalo de propósito -- senão quem digita
 * devagar alternaria entre "editando" e "vendo" na tela do colega.
 */
export const JANELA_DE_EDICAO = 40000;

/** Houve alteração recente o bastante para eu contar como "editando"? */
export const estouEditando = (ultimaAlteracaoEm, agora = Date.now()) => {
    if (!ultimaAlteracaoEm) return false;

    return agora - ultimaAlteracaoEm <= JANELA_DE_EDICAO;
};
